import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

export type EmulatorDeploymentStatus = CTRL_SCHEMA["OpenapigenEmulatorDeploymentStatus"];
export type HealthReport = CTRL_SCHEMA["HealthReport"];
export type RepoLock = CTRL_SCHEMA["OpenapigenRepoLockListEntry"];
export type UserVCSCredential = CTRL_SCHEMA["OpenapigenUserVCSCredentialResponse"];
export type GitLabJob = CTRL_SCHEMA["OpenapigenGitLabJobResponse"];

/** Deployment status (display name, GPU, snapshot, boot step) for many emulators at once. */
export function useEmulatorStatuses(ids: string[], config?: HttpConfiguration) {
  const client = useCtrlClient();
  const key = ids.length ? ["emulators/status", ...[...ids].sort()] : null;
  return useHttp<Record<string, EmulatorDeploymentStatus>>(
    key,
    async () => {
      const results = await Promise.allSettled(ids.map((id) => client.getEmulatorStatus(id)));
      // One emulator's status failing is skipped, but a 401 means the session
      // is gone and nothing failing means nothing to show. Throw those so SWR
      // backs off instead of polling a dead session on every interval.
      const failures = results.flatMap((r) => (r.status === "rejected" ? [r.reason] : []));
      const unauthorized = failures.find((e) => (e as { status?: number })?.status === 401);
      if (unauthorized) throw unauthorized;
      if (failures.length && failures.length === results.length) throw failures[0];
      return Object.fromEntries(
        results.flatMap((r, i) => (r.status === "fulfilled" ? [[ids[i], r.value]] : [])),
      );
    },
    config,
  );
}

export function useEmulatorStatus(id: string | undefined, config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp<EmulatorDeploymentStatus>(
    id ? `emulators/${id}/status` : null,
    () => client.getEmulatorStatus(id!),
    config,
  );
}

export function useReadiness(config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp<HealthReport>("readyz", () => client.getReadiness(), {
    refreshInterval: 15_000,
    ...config,
  });
}

export function useRepoLocks(tenantId: string | undefined, config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp<RepoLock[]>(
    client.token && tenantId ? `tenants/${tenantId}/repolocks` : null,
    () => client.getRepoLocks(tenantId!),
    config,
  );
}

export function useVCSCredentials(userId: string | undefined, config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp<UserVCSCredential[]>(
    client.token && userId ? `users/${userId}/vcs-credentials` : null,
    () => client.getUserVCSCredentials(userId!),
    config,
  );
}

export function useAccountLinkURL(config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp(client.token ? "account/link-url" : null, () => client.getAccountLinkURL(), config);
}

export function useGitLabJob(jobUrl: string | undefined, config?: HttpConfiguration) {
  const client = useCtrlClient();
  return useHttp<GitLabJob>(
    client.token && jobUrl ? ["vcs/gitlab/job", jobUrl] : null,
    () => client.getGitLabJob(jobUrl!),
    { revalidateOnFocus: false, shouldRetryOnError: false, ...config },
  );
}
